import { useState } from 'react';
import { ApiError, importPeriodWorkbook, type WorkbookImportReport } from '../../lib/api.js';
import { t } from '../../lib/i18n.js';

/**
 * Bulk Excel import (pass 8). The month's own export is the template: fill in
 * the figures, upload it here, check what will change, then apply. Nothing
 * changes until **Apply**, and then everything or nothing does.
 */
export function ImportWorkbook({ periodId, onApplied }: { periodId: string; onApplied: () => void }) {
  const [file, setFile] = useState<{ name: string; data: string } | null>(null);
  const [report, setReport] = useState<WorkbookImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const send = async (data: string, dryRun: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const result = await importPeriodWorkbook(periodId, data, dryRun);
      setReport(result.report);
      if (result.report.applied) onApplied();
    } catch (caught) {
      setReport(null);
      setError(caught instanceof ApiError ? caught.message : t("The workbook could not be read."));
    } finally {
      setBusy(false);
    }
  };

  const choose = (chosen: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const data = String(reader.result);
      setFile({ name: chosen.name, data });
      void send(data, true);
    };
    reader.onerror = () => setError(t("The file could not be read on this device."));
    reader.readAsDataURL(chosen);
  };

  return (
    <section aria-labelledby="import-heading" className="mt-6 rounded-md border border-border bg-surface p-5 shadow-card">
      <h2 id="import-heading" className="m-0 text-base font-semibold">
        {t("Import from Excel")}
      </h2>
      <p className="mt-1 text-sm text-content-secondary">
        {t("Export this month, fill in the figures, and upload the workbook here. You will see every change before anything is saved.")}
      </p>
      <label className="mt-3 inline-block rounded-sm border border-border-strong px-3 py-2 text-sm font-medium">
        {t("Choose a workbook")}
        <input
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="sr-only"
          disabled={busy}
          onChange={(event) => {
            const chosen = event.target.files?.[0];
            event.target.value = '';
            if (chosen) choose(chosen);
          }}
        />
      </label>
      {file && <span className="ms-2 text-sm text-content-secondary">{file.name}</span>}

      {error && (
        <p role="alert" className="mt-3 text-sm text-tone-critical">
          {error}
        </p>
      )}

      {report && (
        <div className="mt-4 text-sm" role="status">
          {report.applied ? (
            <p className="m-0 font-semibold">{t("Imported —")}{' '}{report.changes.length}{' '}{t("figures changed.")}</p>
          ) : report.problems.length > 0 ? (
            <>
              <p className="m-0 font-semibold text-tone-critical">
                {t("Nothing was imported. Fix these in the workbook and upload it again:")}
              </p>
              <ul className="mb-0 mt-2 ps-5">
                {report.problems.map((problem, index) => (
                  <li key={index}>
                    {problem.sheet}
                    {problem.row ? t(", row {0}", { 0: problem.row }) : ''}: {problem.message}
                  </li>
                ))}
              </ul>
            </>
          ) : report.changes.length === 0 ? (
            <p className="m-0">{t("The workbook matches what is already recorded. Nothing to import.")}</p>
          ) : (
            <>
              <table className="mt-1 w-full border-collapse text-start">
                <caption className="text-start font-semibold">{t("What will change")}</caption>
                <thead>
                  <tr className="text-content-secondary">
                    <th scope="col" className="py-1 text-start font-medium">{t("Item")}</th>
                    <th scope="col" className="py-1 text-start font-medium">{t("Figure")}</th>
                    <th scope="col" className="py-1 text-end font-medium">{t("Now")}</th>
                    <th scope="col" className="py-1 text-end font-medium">{t("From the workbook")}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.changes.map((change) => (
                    <tr key={`${change.item}-${change.field}`} className="border-t border-border">
                      <td className="py-1">{change.item}</td>
                      <td className="py-1">{change.field}</td>
                      <td className="py-1 text-end">{change.current ?? '—'}</td>
                      <td className="py-1 text-end font-semibold">{change.incoming}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                className="mt-3 rounded-sm bg-ag-purple px-4 py-2 font-medium text-content-inverse disabled:opacity-60"
                disabled={busy || !file}
                onClick={() => file && void send(file.data, false)}
              >
                {t("Apply these changes")}
              </button>
            </>
          )}
          {report.notImported.length > 0 && (
            <details className="mt-3 text-content-secondary">
              <summary>{t("Not imported from a workbook")}</summary>
              <ul className="mb-0 mt-1 ps-5">
                {report.notImported.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}
